/*
  themedToast.js  (hosted payment page, src/utils/themedToast.js)
  ---------------------------------------------------------------
  react-hot-toast, themed from the embedded theme (no React context needed).
  Theme is read synchronously via getWqTheme() (URL/sessionStorage/<html>),
  the same source themedSwal.js and selectTheme.js use, so all three agree.

  Three ways to use it, pick whichever fits the call site (parity with swal):

  1) IMPORT SWAP (least edits) - change only the import line, keep every
     existing toast.* call exactly as-is:
        import { WqToast as toast } from "../../utils/themedToast";
        ...
        toast.success("Transaction cancelled");
        toast.error("Nothing to copy");

  2) DROP-IN FUNCTION - same call shape as toast():
        import { themedToast } from "../../utils/themedToast";
        themedToast("Saved");                 // blank toast
        themedToast.success("Done");          // typed toast

  3) HOOK (parity with the swal hook):
        const { toast } = useThemeToast();
        toast.success("Done");

  Light mode is a pure pass-through: react-hot-toast keeps its own defaults and
  anything you set on <Toaster toastOptions> still applies untouched. Only when
  the host passed theme=dark do we inject the dark box style (and a readable
  loading spinner). Per-call style/iconTheme you pass always wins on conflict.
*/

import baseToast from "react-hot-toast";
import { getWqTheme } from "./selectTheme";

const isDark = getWqTheme() === "dark";

// Dark toast surface. Mirrors the swal popup colours for a consistent look.
const DARK_STYLE = {
  background: "#1f2937",
  color: "#f3f4f6",
  border: "1px solid #374151",
};

// Success (green) and error (red) badges read fine on the dark surface, so we
// leave their icons alone and only restyle the box. The loading spinner is the
// exception: its faint default track disappears on dark, so give it contrast.
const DARK_LOADING_ICON = { primary: "#e5e7eb", secondary: "#4b5563" };

// Merge helper. In light mode we pass the caller's options straight through so
// nothing clobbers react-hot-toast defaults or <Toaster> global options. In
// dark we layer the dark box style under the caller's own style.
const merge = isDark
  ? (opts = {}) => ({
      ...opts,
      style: { ...DARK_STYLE, ...(opts.style || {}) },
    })
  : (opts) => opts;

// Loading needs the extra icon treatment in dark, only if the caller did not
// already supply their own iconTheme.
const mergeLoading = isDark
  ? (opts = {}) => {
      const m = merge(opts);
      if (!opts.iconTheme) m.iconTheme = DARK_LOADING_ICON;
      return m;
    }
  : (opts) => opts;

/*
  WqToast mirrors the react-hot-toast API surface: callable for a blank toast,
  with .success/.error/.loading/.custom/.promise, and the imperative controls
  (.dismiss/.remove) passed through unchanged so existing ids keep working.
*/
export const WqToast = (message, opts) => baseToast(message, merge(opts));

WqToast.success = (message, opts) => baseToast.success(message, merge(opts));
WqToast.error = (message, opts) => baseToast.error(message, merge(opts));
WqToast.loading = (message, opts) => baseToast.loading(message, mergeLoading(opts));
WqToast.custom = (jsx, opts) => baseToast.custom(jsx, merge(opts));
WqToast.promise = (promise, msgs, opts) => baseToast.promise(promise, msgs, merge(opts));

// Imperative controls take ids, not style, so they pass through verbatim.
WqToast.dismiss = baseToast.dismiss;
WqToast.remove = baseToast.remove;

// Dynamic drop-in that reads like a function call.
export const themedToast = WqToast;

// Parity with the swal hook API.
export const useThemeToast = () => ({ toast: WqToast });

export default WqToast;